import React from 'react'
import styles from './notifications.module.scss'
import i18n from 'constants/strings'
import { useDispatch, useSelector } from 'react-redux'
import { AddOrManagePlayerModalMode } from 'constants/modals'
import { AppState } from 'redux/reducers'
import { ManagePlayersInWatchlistsOperation, InteractiveNotification, Player } from 'types/global'
import { setManageModalState } from 'actions/watchlist-actions'
import { MAX_PLAYERS_PER_WATCHLIST, MAX_WATCHLISTS_LENGTH } from 'constants/watchlists'
import { Trans } from 'react-i18next'

export interface Context {
  player: Player
  operationTypes: ManagePlayersInWatchlistsOperation[]
  interestedWatchlistRefs: string[]
}

interface Props extends InteractiveNotification<Context> {
  closeToast?: () => void
}

export const withManagePlayer = (notification: InteractiveNotification<Context>) => (
  <ManagePlayerInWatchlist {...notification} />
)

const TRANS_STR_BASE = 'strAlerts.managingPlayerWl'

const ManagePlayerInWatchlist = ({
  context: { player, operationTypes, interestedWatchlistRefs },
  type,
  closeToast,
}: Props) => {
  const dispatch = useDispatch()
  const watchlists = useSelector((state: AppState) => state.watchlists.watchlists)
  const watchlistsPlayerIdsStore = useSelector((state: AppState) => state.watchlists.playerIdsStore)
  const includedWatchlistsIds = watchlistsPlayerIdsStore[player.id] ?? []
  const isError = type === 'error'
  const isMultiOp = operationTypes.length > 1
  const isMultiTarget = interestedWatchlistRefs.length > 1
  const operationKey = isMultiOp ? 'update' : operationTypes[0]
  const isCreateError = operationKey === 'create' && isError
  const isTooManyPlayers =
    isError &&
    operationKey === 'add' &&
    interestedWatchlistRefs.every(
      id => watchlists[id] && watchlists[id].playerIds.length >= MAX_PLAYERS_PER_WATCHLIST
    )
  const isTooManyWatchlists =
    isCreateError && Object.keys(watchlists).length >= MAX_WATCHLISTS_LENGTH
  const hasManageBtn = operationKey !== 'remove' && (!isError || isTooManyPlayers)

  const getNotificationText = () => {
    // if we have successfully created a watchlist and added a player to it, the notification message will be the one for adding a player
    const keyBase = operationKey === 'create' && type === 'success' ? 'add' : operationKey
    let transKey = `${TRANS_STR_BASE}.${type}.${keyBase}`
    let vars: { name?: string; count?: number }

    if (isMultiTarget) {
      transKey = transKey + 'Plural'
      vars = { count: interestedWatchlistRefs.length }
    } else {
      const ref = interestedWatchlistRefs[0] ?? ''
      // if the operation performed is to create a new watchlist, we might not have the name in the store - or even the id if the operation was not successful - so we use the reference passed instead, which will be the name
      const name = isCreateError ? ref : watchlists[ref]?.name
      vars = {
        name: name ?? '',
      }
    }

    if (isTooManyPlayers || isTooManyWatchlists) transKey = `${transKey}Full`

    if (isTooManyPlayers) {
      const [ns, ...rest] = transKey.split('.')

      return (
        <Trans
          ns={ns}
          i18nKey={rest.join('.')}
          values={vars}
          components={{
            0: <></>,
            1: <strong>Player can’t be added</strong>,
            2: <br />,
          }}
        />
      )
    }

    return i18n.t(transKey, vars)
  }

  const text = getNotificationText()

  const handleClick = () => {
    closeToast?.()
    dispatch(
      setManageModalState({
        player,
        includedWatchlistsIds,
        mode: AddOrManagePlayerModalMode.MANAGE,
        isOpen: true,
      })
    )
  }

  return (
    <>
      {text}
      {hasManageBtn && (
        <button onClick={handleClick} className={styles.actionBtn}>
          {i18n.t('strCommonUI.button.manage')}
        </button>
      )}
    </>
  )
}

export default ManagePlayerInWatchlist
